import type { BoxProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Container,
  Heading,
  IconButton,
  SimpleGrid,
  calc,
  cssVariables,
} from '@hover/blueprint';
import { iChevronLeft, iChevronRight } from '@hover/icons';
import React from 'react';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { hideScrollbars, withHover } from 'src/shared/utils/styles';

import { useIdeas } from './IdeasSection/useIdeas';

const SimpleGridHideScroll = hideScrollbars(SimpleGrid);

export const PROJECT_DETAILS_PADDING = { base: 500, tablet: 700, desktop: 800 };
export const PROJECT_DETAILS_PADDING_WITH_WIDE = {
  ...PROJECT_DETAILS_PADDING,
  width500: 'unset',
};

interface HeaderSectionProps extends BoxProps {
  title: string;
  subtitle?: string;
}

export const HeaderSection = ({
  title,
  subtitle,
  ...props
}: HeaderSectionProps) => (
  <Box flexDir="column" px={PROJECT_DETAILS_PADDING} {...props}>
    <Heading mb={0} mr={100} mt={0} size={100}>
      {title}
    </Heading>
    {subtitle && (
      <Body color="neutral.600" mb={500} mt={0} size={300}>
        {subtitle}
      </Body>
    )}
  </Box>
);

interface ProjectCardSectionProps extends BoxProps {
  children: React.ReactNode;
}

export const ProjectCardSection = ({
  children,
  ...props
}: ProjectCardSectionProps) => (
  <SimpleGridHideScroll
    boxSizing="border-box"
    columns={3}
    display={{ base: 'flex', desktop: 'grid' }}
    flexWrap="nowrap"
    gap={{ base: 200, width275: 300, desktop: 500 }}
    mt="0px !important"
    overflowX={{ base: 'auto', desktop: 'visible' }}
    px={PROJECT_DETAILS_PADDING}
    scrollSnapType="x mandatory"
    width="100%"
    {...props}
  >
    {children}
  </SimpleGridHideScroll>
);

export const ProjectCardWidth = {
  base: '100%',
  width275: calc.subtract('50%', calc.divide(cssVariables.space(300), 2)),
  desktop: 'auto',
};

export const ProjectCardWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <Container m={0} minWidth={ProjectCardWidth} p={0} width={ProjectCardWidth}>
    {children}
  </Container>
);

export const HeaderSectionButtons = ({ ...props }) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { canScrollLeft, canScrollRight, scroll } = useIdeas();

  return (
    <Box
      display="none"
      gap={200}
      mb={500}
      px={PROJECT_DETAILS_PADDING}
      sx={{ ...withHover({ display: { base: 'flex', desktop: 'none' } }) }}
      {...props}
    >
      <IconButton
        icon={iChevronLeft}
        isDisabled={!canScrollLeft}
        label={t('projects.ideas.scrollLeft')}
        onClick={() => scroll('previous')}
        variant="ghost"
      />
      <IconButton
        icon={iChevronRight}
        isDisabled={!canScrollRight}
        label={t('projects.ideas.scrollRight')}
        onClick={() => scroll('next')}
        variant="ghost"
      />
    </Box>
  );
};
